import React, { useEffect, useRef, useState } from 'react';

/**
 * Counts a stat value up from 0 the first time it scrolls into view.
 * Accepts strings like "50+", "10K+", "99.8%", "24/7" — it animates the
 * leading number and preserves any prefix/suffix (falls back to the raw
 * string when there's no clean number to animate, e.g. "24/7").
 */
const parse = (value) => {
  const str = String(value);
  const match = str.match(/^(\D*)(\d+(?:\.\d+)?)(.*)$/s);
  if (!match) return null;
  const [, prefix, num, suffix] = match;
  // Skip things like "24/7" where the suffix carries another number.
  if (/\d/.test(suffix)) return null;
  return { prefix, target: parseFloat(num), decimals: (num.split('.')[1] || '').length, suffix };
};

const CountUp = ({ value, duration = 1600, className = '' }) => {
  const ref = useRef(null);
  const parsed = parse(value);
  const target = parsed ? parsed.target : null;
  const [inView, setInView] = useState(false);
  const [display, setDisplay] = useState(0);

  // Mark the element the first time it enters the viewport.
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Run the count-up once in view (re-runs only if the target value changes).
  useEffect(() => {
    if (!inView || target === null) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(Math.max((now - start) / duration, 0), 1);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setDisplay(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
      else setDisplay(target);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [inView, target, duration]);

  if (!parsed) return <span ref={ref} className={className}>{value}</span>;

  return (
    <span ref={ref} className={className}>
      {parsed.prefix}
      {display.toFixed(parsed.decimals)}
      {parsed.suffix}
    </span>
  );
};

export default CountUp;
